/* eslint-disable max-lines-per-function */
import {
  type ComponentProps,
  type ReactElement,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';

import {
  PassengersTable,
  type PassengersTableProps,
  type Passenger,
} from '@fz/ui-components-react';
import { useTranslation } from 'react-i18next';

import { type NotCheckedInPax } from '@models/ui/pnr-data-model';

import {
  getSingleAdultOrAdultWithInfantId,
  mapNotCheckedInPassengers,
} from '@helper/passenger-table-view.mapper';

import { BaggageIcon, HandBaggageIcon, NaIcon, SeatFeatureIcon } from '@shared/icons';

import { logger } from '@utils/logger.util';
import { SeatFeatureIconGrey } from '@shared/icons/SeatFeatureIconGrey';
import { HandBaggageIconGrey } from '@shared/icons/HandBaggageIconGrey';
import { getResource } from '@utils/resource-translator.util';
import { scrollErrorIntoView } from './scroll-error-into-view.util';

interface PassengersTableComponentProps {
  notCheckedInPaxList?: NotCheckedInPax[];
  groupPnrFlow?: boolean;

  isLoading: boolean;

  setPassengerSelect: (selectedIds: string[]) => void;

  onSelectedPassengersChange?: (selectedIds: string[]) => void;

  errorMessage?: string;
  isTabletView?: boolean;
  variant?: ComponentProps<typeof PassengersTable>['variant'] | 'none';
}

export interface PassengersTableComponentHandle {
  focusError: () => void;
}

export const PassengersTableComponentInner = (
  {
    notCheckedInPaxList,

    isLoading,

    onSelectedPassengersChange,
    setPassengerSelect,
    groupPnrFlow = false,
    errorMessage = '',
    isTabletView = false,
    variant = 'none',
  }: PassengersTableComponentProps,
  ref: React.Ref<PassengersTableComponentHandle>,
): ReactElement | null => {
  const { t, i18n } = useTranslation();
  const [selectedPassengerIds, setSelectedPassengerIds] = useState<string[]>([]);
  const errorContainerRef = useRef<HTMLDivElement>(null);
  const errorMessageValue = useMemo(
    () => getResource(errorMessage),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [errorMessage, i18n.language],
  );

  // Imperative so it re-triggers even when the error message text is unchanged between clicks
  useImperativeHandle(ref, () => ({
    focusError: (): void => {
      if (!errorContainerRef.current) {
        logger.warn('PassengersTable: errorContainerRef is not available');
        return;
      }

      const element = errorContainerRef.current;
      scrollErrorIntoView(element);
      element.focus({ preventScroll: true });
    },
  }));

  const passengerList: Passenger[] = useMemo(() => {
    const mapped = notCheckedInPaxList ? mapNotCheckedInPassengers(notCheckedInPaxList) : [];
    return mapped;
  }, [notCheckedInPaxList]);

  // Handle passenger selection
  const handleSelectPassenger = useCallback(
    (passengerId: string, isSelected: boolean) => {
      const adultPassengerDetail = [passengerId];
      const isInfant = passengerList.find((passenger) => passenger.id === passengerId)?.isInfant;
      if (isInfant) {
        const adultPassengerId = passengerList.find(
          (passenger) => passenger.associatedInfantID === passengerId,
        )?.id;
        adultPassengerId && adultPassengerDetail.push(adultPassengerId);
      }
      setSelectedPassengerIds((prev) => {
        const next = isSelected
          ? [...prev, ...adultPassengerDetail]
          : prev.filter((id) => !adultPassengerDetail.includes(id));
        logger.info('Passenger selection changed', { passengerId, isSelected, selectedIds: next });
        // Notify parent component if callback provided
        onSelectedPassengersChange?.(next);
        return next;
      });
    },
    [onSelectedPassengersChange, passengerList],
  );

  logger.info('Mapped not checked-in passengers for PassengersTableComponent', { passengerList });

  // Use original NotCheckedInPax data for auto-selection to access passengerType enum
  const passList = useMemo(
    () => [
      ...getSingleAdultOrAdultWithInfantId(
        (notCheckedInPaxList ?? []).map((p) => ({ id: p.paxId, passengerType: p.passengerType })),
      ),
    ],
    [notCheckedInPaxList],
  );
  const autoSelectedPassengerIds = useMemo(
    () => (groupPnrFlow ? passengerList.map((passenger) => passenger.id) : passList),
    [groupPnrFlow, passengerList, passList],
  );

  useEffect(() => {
    setSelectedPassengerIds([...autoSelectedPassengerIds]);
    setPassengerSelect([...autoSelectedPassengerIds]);
  }, [autoSelectedPassengerIds, setPassengerSelect]);

  if (isLoading || !notCheckedInPaxList) return null;

  const headerLabels: string[] = [
    t('SelectPassenger.Passengers.Text'),
    t('SelectPassenger.Class.Text'),
    t('Seat.Text'),
    t('SelectPassenger.Baggage.Text'),
    t('Status.Text'),
  ];

  // Get selected passenger objects (if needed downstream)

  const paxTableProps: PassengersTableProps = {
    Skeleton: isLoading,

    errorMessage: errorMessageValue,

    passengers: passengerList,

    selectedPassengers: selectedPassengerIds,

    onSelectPassenger: handleSelectPassenger,

    multiSelect: true,
    naIcon: <NaIcon />,

    sameDayReturn: notCheckedInPaxList?.[0]?.sameDayReturn,

    compHeaderKey: t('SelectPassenger.PageTitle.Text'),

    isId90: notCheckedInPaxList?.[0]?.isId90,

    icons: [
      <SeatFeatureIcon key="SeatFeatureIcon" className="seat-page-icon" />,
      <HandBaggageIcon key="HandBaggageIcon" />,
      <BaggageIcon key="BaggageIcon" />,
      <SeatFeatureIconGrey key={'SeatFeatureIconGrey'} />,
      <HandBaggageIconGrey key={'HandBaggageIconGrey'} />,
    ],

    HeaderTranslationKey: headerLabels,
    className: 'passenger-check',

    cellTranslationKey: {
      Handbaggage: t('Extras.HandBaggage.Text'),

      Checkedbaggage: t('Extras.CheckedBaggage.Text'),

      infantCheckedbaggage: t('SelectPassenger.NotApplCheckedInBaggage.Text'),
      AdditionalBaggage: t('Review.Baggage.Additional.Text'),
    },
    SeatNotSelectedKey: [
      `${t('passengerList.seatNotSelected.Text')}`,
      `${t('SelectPassenger.Minus.Text')}`,
    ],
    InfantSeatTranslationKey: t('SelectPassenger.NotApplSeat.Tooltip.Description.Text'),
  };

  if (variant !== 'none') {
    paxTableProps.variant = variant;
  }

  return (
    <>
      {passengerList.length > 0 && (
        <div ref={errorContainerRef} tabIndex={-1} className="passengers-table-error-focus">
          <PassengersTable {...paxTableProps} isTabletView={isTabletView} />
        </div>
      )}
    </>
  );
};
export const PassengersTableComponent = forwardRef<
  PassengersTableComponentHandle,
  PassengersTableComponentProps
>(PassengersTableComponentInner);
